// 1mm ≈ 2.8346pt; react-pdf operates in points.
export const mm = (n: number): number => +(n * 2.8346).toFixed(2);

export const colors = {
    // Ink scale — 95% of the page uses these
    ink00: '#0B1221',
    ink10: '#1F2937',
    ink20: '#4B5563',
    ink40: '#6B7280',
    ink60: '#9CA3AF',
    ink80: '#D1D5DB',

    // Rules / surfaces
    rule: '#E5E7EB',
    ruleSoft: '#F0EFE9',
    paper: '#FFFFFF',
    surface: '#FAFAF7',
    surfaceAlt: '#F5F4EE',

    // Cover page (editorial dark — independent of body palette)
    cover: '#0B1221',
    coverInk: '#FFFFFF',
    coverInkSoft: '#D1D5DB',
    coverInkMuted: '#9CA3AF',
    coverRule: '#0F766E',

    // Single accent (Recur purple)
    accent: '#6021B3',
    accentSoft: '#F2E8FF',
    accentDeep: '#371366',

    // Semantic (status only)
    pos: '#059669',
    posSoft: '#D1FAE5',
    warn: '#D97706',
    warnSoft: '#FEF3C7',
    neg: '#DC2626',
    negSoft: '#FEF2F2',
    sev: '#991B1B',
    sevSoft: '#FEE2E2',
    neutralSoft: '#F3F4F6',

    // Chart categorical palette
    c1: '#0F766E',
    c2: '#1E3A8A',
    c3: '#B45309',
    c4: '#7C2D12',
    c5: '#374151',

    // Back-compat aliases (old field names still referenced by some primitives)
    ink: '#0B1221',
    brand: '#6021B3',
    brandDark: '#371366',
    brandSoft: '#F2E8FF',
    brandAccent: '#6021B3',
    inkSoft: '#1F2937',
    muted: '#4B5563',
    mutedSoft: '#6B7280',
    hairline: '#E5E7EB',
    hairlineSoft: '#F0EFE9',
    surfaceTint: '#FAFAF7',
    info: '#1E3A8A',
    infoSoft: '#DBEAFE',
} as const;

export const severity = {
    SEVERE: { fg: colors.sev, bg: colors.sevSoft, label: 'Severe' },
    HIGH: { fg: colors.neg, bg: colors.negSoft, label: 'High' },
    MEDIUM: { fg: colors.warn, bg: colors.warnSoft, label: 'Medium' },
    LOW: { fg: colors.pos, bg: colors.posSoft, label: 'Low' },
} as const;

// Type scale in points. Sample/spec uses pt directly; print-appropriate density.
export const typeScale = {
    displayH1: 42,
    displayH2: 22,
    displayH3: 16,
    h4: 10,
    h4Mini: 9,
    body: 9,
    bodyLg: 9.5,
    bodySm: 8,
    caption: 7.5,
    micro: 6.5,
    eyebrow: 8,
    metricLg: 18,
    metricMd: 13,
    metricSm: 10,
    tableCell: 8.5,
    tableHead: 7.5,
    // legacy aliases kept so existing modules don't need to be touched
    display: 42,
    h1: 22,
    h2: 16,
    h3: 12,
} as const;

export const space = {
    sp1: mm(1),
    sp2: mm(2),
    sp3: mm(3),
    sp4: mm(4),
    sp6: mm(6),
    sp8: mm(8),
    sp12: mm(12),
    // legacy aliases
    xs: mm(1),
    sm: mm(2),
    md: mm(3),
    lg: mm(4),
    xl: mm(6),
    xxl: mm(8),
} as const;

export const page = {
    width: 595.28, // A4 width (210mm)
    height: 841.89, // A4 height (297mm)
    // Padding sets the bounds of *flow* content. The running header/footer
    // are absolutely-positioned, so flow content that overflows past padding
    // will visually clash with them. Keep padding conservative — the cost is
    // ~17pt fewer content points per page; the win is no footer-rule bleed.
    // Per PM feedback: dropped horizontal padding 16mm → 12mm. That gives
    // every page ~22pt of extra content width (504 → 526pt) which lets long
    // table cells (legal cases, MCA charges, audited statements) wrap to
    // fewer lines so the row rhythm doesn't get blown out. Vertical padding
    // unchanged — those values are tuned for the running header/footer
    // safety zones.
    paddingTop: mm(28), // 79pt — leaves ~37pt safety below header rule
    paddingRight: mm(12),
    paddingBottom: mm(28), // 79pt — leaves ~27pt safety above footer rule
    paddingLeft: mm(12),
    headerOffset: mm(10),
    footerOffset: mm(12),
    gutter: mm(4),
    margin: mm(16), // legacy
} as const;

export const radii = {
    sm: 2,
    md: 4,
    lg: 8,
} as const;

// NOTE: Body/mono are pinned to Inter (known-working TTF on gstatic). Fraunces is optional display only.
// Using WOFF for body would cause random character-overlap when fontkit fails to decompress a weight.
export const fontFamily = {
    display: 'Fraunces',
    body: 'Inter',
    bodyMedium: 'Inter-Medium',
    bodyBold: 'Inter-Bold',
    mono: 'Inter',
    // legacy aliases
    sans: 'Inter',
    sansMedium: 'Inter-Medium',
    sansBold: 'Inter-Bold',
    sansOblique: 'Inter',
} as const;

export type Severity = keyof typeof severity;

// Cover page — single source of truth for both PDF and screen renderers.
// Values are in PDF points (1pt = 4/3 px). Screen renderer scales via ptToPx().
// Letter-spacing values are unitless em ratios (relative to the size they apply to).
export const ptToPx = (pt: number): number => +(pt * (4 / 3)).toFixed(2);

export const coverDesign = {
    // Layout (pt). Page height matches A4 portrait (595 × 842pt) so the
    // screen preview renders at the exact same aspect ratio as the exported PDF.
    pageWidth: 595,
    pageHeight: 842,
    paddingTop: 71,
    paddingBottom: 62,
    paddingX: 71,
    topToTitle: 142,

    // Title
    // line-heights ≥1.1 — react-pdf clips ascenders/descenders below ~1.05.
    titleSize: 42,
    titleLineHeight: 1.1,
    titleTrackingEm: -0.025,

    suffixSize: 34,
    suffixLineHeight: 1.15,
    suffixTrackingEm: -0.02,
    suffixMarginTop: 1,

    // Hairline rule
    ruleWidth: 113,
    ruleHeight: 0.75,
    ruleMarginTop: 17,
    ruleMarginBottom: 11,

    // Metadata rows
    metaRowHeight: 18,
    // Label width sets the gutter between heading (CIN/PAN/SECTOR/etc.) and value.
    // 68pt gives clear horizontal separation while still fitting "AICA SCORE" comfortably.
    metaLabelWidth: 68,
    metaLabelSize: 7.5,
    metaLabelTrackingEm: 0.1,
    metaValueSize: 10,

    // Bottom block
    bottomLabelSize: 11,
    bottomLabelTrackingEm: 0.22,
    bottomGap: 5,
    bottomDateSize: 9,
    bottomDateTrackingEm: 0.05,

    // "Powered by AICA" wordmark
    poweredBySize: 9,
    aicaWordmarkSize: 13,
    aicaWordmarkTrackingEm: 0.08,
    poweredByGap: 4,
} as const;

export const coverFonts = {
    pdf: {
        display: 'Fraunces',
        sans: 'Geist',
        mono: 'GeistMono',
    },
    screen: {
        display: "'Fraunces', Georgia, serif",
        sans: "'Geist', system-ui, sans-serif",
        mono: "'Geist Mono', 'IBM Plex Mono', ui-monospace, monospace",
    },
} as const;

// Vertical breathing room between top-level sections inside a single PdfSection.
// Used uniformly by Overview → News → Sector → Competitors. Bumping this alone
// changes the rhythm of the entire report; both PDF and screen consume it.
export const interSectionGap = 36;

// Module-level palette aliases so Overview/News/Competitors stop reading raw hex.
// All values map to the existing `colors` table; this just adds semantic names.
export const overviewColors = {
    surface: colors.surface,
    surfaceAlt: colors.surfaceAlt,
    border: colors.rule,
    borderSoft: colors.ruleSoft,
    ink: colors.ink00,
    inkDeep: colors.ink10,
    inkSoft: colors.ink20,
    inkMuted: colors.ink40,
    inkSubtle: colors.ink60,
    accent: '#0F766E',
    accentSoft: '#CCFBF1',
    accentDeep: '#064E3B',
    accentSurface: 'rgba(204, 251, 241, 0.35)',
    pos: colors.pos,
    posSoft: colors.posSoft,
    neg: colors.neg,
    negSoft: colors.negSoft,
    warn: colors.warn,
    warnSoft: colors.warnSoft,
    sev: colors.sev,
    sevSoft: colors.sevSoft,
} as const;

// Overview / News / Competitors design tokens. Values in pt (PDF native).
// Screen renderer scales via ptToPx() — both stay perfectly in sync.
export const overviewDesign = {
    // Section vertical rhythm — gap above each H3 (About / Business Profile / Basic
    // Details / Risk Grade). Bumped from 5 → 12pt so adjacent sections breathe.
    sectionGap: 12,
    headingToBody: 6,

    // Heading 3 (section labels: About / Business Profile / Basic Details / Risk Grade)
    h3Size: 10,
    h3Color: 'ink',

    // Identity card (top of Overview)
    idCardPadX: 11.5,
    idCardPadY: 11.5,
    idCardRadius: 3,
    idCardGap: 8.5,
    idLogoSize: 45,
    idLogoRadius: 1.5,
    idLogoTextSize: 17,
    idTitleSize: 16,
    idTitleLineHeight: 1.5,
    idChipPadX: 5.5,
    idChipPadY: 1.5,
    idChipRadius: 1.5,
    idChipSize: 7,
    idChipTrackingEm: 0.05,
    idChipGap: 4.5,
    idSubtitleSize: 7.5,
    idSubtitleLineHeight: 1.5,
    idSubtitleColor: 'inkMuted',

    // About / body paragraph
    // Tightened lineHeight 1.6 → 1.45 + bumped char cap 600 → 950 so the
    // about text reads as a substantive paragraph instead of a stub. The
    // tighter leading lets two extra lines of content fit in the same block
    // height the previous draft used.
    bodySize: 9.5,
    bodyLineHeight: 1.45,
    bodyColor: 'inkDeep',
    aboutMaxChars: 950,

    // Business Profile cards (3-col)
    bpGap: 8.5,
    bpCardPad: 12,
    bpCardRadius: 3,
    bpCardBorder: 0.5,
    bpTitleSize: 9,
    bpTitleColor: 'ink',
    bpBodySize: 8,
    bpBodyLineHeight: 1.4, // was 1.55 — tighter so more text fits per card
    bpBodyColor: 'inkSoft',
    bpBodyMaxChars: 360, // was 240 — most product / customer descriptions now land complete

    // Basic Details card
    bdCardPad: 9,
    bdCardRadius: 3,
    bdCardBorder: 0.5,
    bdRowGap: 5.5,
    bdItemGap: 8.5,
    bdLabelSize: 6.5,
    bdLabelColor: 'inkMuted',
    bdLabelTrackingEm: 0.08,
    bdValueSize: 8,
    bdValueColor: 'ink',
    bdAddressSize: 8,
    bdAddressColor: 'inkMuted',
    bdAddressLineHeight: 1.55,

    // Risk Grade card
    rgCardPad: 12,
    rgCardRadius: 3,
    rgCardBorder: 0.5,
    rgGap: 11.5,
    rgBadgeSize: 18,
    rgBadgeTrackingEm: -0.02,
    rgBarHeight: 4.5,
    rgBarWidth: 80,
    rgBarRadius: 2.25,
    rgDescSize: 7.5,
    rgDescLineHeight: 1.5,
    rgDescColor: 'inkMuted',
    rgPillarRowHeight: 14,
    rgPillarLabelSize: 8,
    rgPillarLabelColor: 'inkSoft',
    rgPillarBandSize: 8,
    rgPillarDotSize: 4,
} as const;

// News & Updates + Sector Outlook
export const newsDesign = {
    introSize: 7.5,
    introColor: 'inkMuted',
    introLineHeight: 1.5,

    // Card grid (2 cols). Bumped from 8.5 → 14pt for clearer breathing room between
    // news cards in PDF — at 8.5pt the borders read as a single block at A4 zoom.
    gridGap: 14,
    cardPad: 9.5,
    cardRadius: 3,
    cardBorder: 0.5,
    cardGap: 3,

    sourcePillSize: 20,
    sourcePillRadius: 10,
    sourcePillBg: 'surfaceAlt',
    sourcePillTextSize: 6,
    sourcePillTextColor: 'inkSoft',
    sourceLabelSize: 8,
    sourceLabelColor: 'inkSoft',
    sourceGap: 5.5,

    sentChipPadX: 5.5,
    sentChipPadY: 1.5,
    sentChipRadius: 1.5,
    sentChipSize: 7,
    sentChipTrackingEm: 0.05,

    headlineSize: 8,
    headlineColor: 'ink',
    headlineLineHeight: 1.55,

    dateSize: 7.5,
    dateColor: 'inkMuted',

    // Sector outlook
    sectorGap: 6,
    sectorMetricPad: 9,
    sectorMetricLabelSize: 7.5,
    sectorMetricLabelTrackingEm: 0.08,
    sectorMetricLabelColor: 'inkMuted',
    sectorMetricValueSize: 10,
    sectorMetricValueColor: 'ink',
    sectorMetricUnitSize: 11,
    sectorMetricUnitColor: 'inkMuted',
    sectorMetricSubSize: 7.5,
    sectorMetricSubColor: 'inkMuted',

    sectorBodySize: 8,
    sectorBodyColor: 'inkSoft',
    sectorBodyLineHeight: 1.55,

    // Drivers / Risks card
    drCardBg: 'surface',
    drCardPad: 8.5,
    drCardRadius: 3,
    drListItemPadY: 5.5,
    drListItemPadL: 17,
    drListBorder: 0.5,
    drListBorderColor: 'borderSoft',
    drDashColor: 'accent',
    drDashSize: 10,
    drItemSize: 8.5,
    drItemColor: 'inkDeep',
    drHeaderSize: 6.5,
    drHeaderTrackingEm: 0.08,
    drGreenColor: 'pos',
    drRedColor: 'neg',
} as const;

// Key Competitors. Two layouts coexist:
//   • cards   — current production layout (Figma 131:85946). 4-up grid with
//               subject company highlighted via teal left border + tinted bg.
//   • table   — legacy comparison table. Kept in code so it can be re-enabled
//               by swapping a single render line in Overview.
export const competitorsDesign = {
    // ── Cards layout (active, Figma 131:85949) ──
    //
    // Figma values are in PIXELS; tokens here are in POINTS (PDF native).
    // ptToPx() upscales by 4/3 for the screen side, so storing the raw Figma
    // pixel value here would make the screen render 1.33× too big — exactly
    // what we hit on first pass. Convert px → pt by × 0.75 before storing.
    cardGap: 8.5, // 11.34px Figma
    cardPad: 9.25, // 12.34px Figma
    cardRadius: 3, // 4px Figma
    cardBorder: 0.4, // 0.5px Figma
    cardBorderColor: 'borderSoft',
    cardSubjectBorderLeft: 1.5, // 2px Figma
    cardSubjectBorderColor: 'accent',
    cardSubjectBg: 'accentSurface',

    // Font sizes dialled down vs the original Figma px values (which translated
    // to 9pt name / 8pt desc) so that long entity names like
    // "Arya.ag (Arya Collateral Warehousing Services Private Limited / …)"
    // fit without ballooning the card height. The 2-column grid below also
    // gives each card ~2× the horizontal room, so smaller fonts pack more
    // content per line.
    // Per PM feedback: stop truncating mid-name. Tightened line heights so
    // a 3-line title fits in roughly the same vertical space the previous
    // 2-line title used, and bumped the char cap so 95%+ of real entity
    // names land within the card without ellipsis.
    // Bumped 7.5 → 9pt so company names read at parity with the rest of the
    // body type. Compensated by keeping lineHeight tight (1.2) and trusting
    // the 3-line minHeight reservation to keep cards aligned even when a
    // long parenthetical alias wraps.
    cardNameSize: 9,
    cardNameLineHeight: 1.2,
    cardNameColor: 'inkDeep',
    cardNameMaxLines: 3,
    cardNameTruncate: 110,

    cardKickerSize: 6,
    cardKickerTrackingEm: 0.08,
    cardKickerColor: 'inkMuted',
    cardKickerSubjectColor: 'accent',
    cardKickerGap: 2.8,

    cardDescSize: 7,
    cardDescColor: 'inkSoft',
    cardDescLineHeight: 1.4, // was 1.5 — tighter so more chars per card
    cardDescGap: 5.65,
    cardDescMaxLines: 5, // was 4 — accommodates the longer descriptions
    cardDescTruncate: 260, // was 180 — almost always fits without ellipsis

    cardDividerColor: '#F0EFE9',
    cardRowSize: 7.5, // 10px Figma
    cardRowLineHeight: 1.5,
    cardRowLabelColor: 'inkMuted',
    cardRowValueColor: 'inkDeep',
    cardRowPadY: 4.25, // 5.67px Figma

    // ── Table layout (legacy, kept) ──
    rowMinHeight: 16,
    cellPadX: 5.5,
    cellPadY: 4.5,
    border: 0.5,
    borderColor: 'borderSoft',
    headerBorder: 0.75,
    headerBorderColor: 'inkDeep',

    labelColWidth: 68,
    labelSize: 8,
    labelColor: 'inkMuted',
    labelTrackingEm: 0.08,

    headerSize: 7,
    headerColor: 'inkMuted',
    headerTrackingEm: 0.08,
    headerFocalColor: 'accentDeep',

    cellSize: 8,
    cellColor: 'inkDeep',
    cellLineHeight: 1.55,
    cellMutedColor: 'inkMuted',

    focalBg: 'accentSurface',
    focalHeaderBg: 'accentSoft',
    focalHeaderBorder: 'accent',
} as const;
